import React, { useState } from 'react';
import { Download, Calendar, FileText, FileSpreadsheet, X, ChevronLeft } from 'lucide-react';
import { Button } from '@vaiu/ui/button';
import { Label } from '@vaiu/ui/label';
import { RadioGroup, RadioGroupItem } from '@vaiu/ui/radio-group';
import { Calendar as CalendarComponent } from '@vaiu/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@vaiu/ui/popover';
import { Checkbox } from '@vaiu/ui/checkbox';
import { format } from 'date-fns';
import { DateRange } from 'react-day-picker';

interface TransactionExportProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (format: 'csv' | 'pdf', filters?: any) => Promise<void>;
  filters: any;
}

const TransactionExport: React.FC<TransactionExportProps> = ({
  isOpen,
  onClose,
  onExport,
  filters
}) => {
  const [exportFormat, setExportFormat] = useState<'csv' | 'pdf'>('csv');
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 days ago
    to: new Date(),
  });
  const [includeFilters, setIncludeFilters] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    try {
      setIsExporting(true);
      
      const exportFilters = {
        ...(includeFilters ? filters : {}),
        startDate: dateRange?.from?.toISOString().split('T')[0],
        endDate: dateRange?.to?.toISOString().split('T')[0],
      };
      
      await onExport(exportFormat, exportFilters);
      onClose();
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setIsExporting(false);
    }
  };

  const getActiveFiltersCount = () => {
    return Object.keys(filters).length;
  };

  // Don't render anything if not open
  if (!isOpen) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div className="flex items-center space-x-2">
            <Download className="w-5 h-5" />
            <h2 className="text-xl font-semibold">Export Transactions</h2>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">
        Choose export format and customize the data to include
      </p>

      <div className="space-y-6">
        {/* Export Format */}
        <div className="space-y-3">
          <Label className="text-base font-medium">Export Format</Label>
          <RadioGroup
            value={exportFormat}
            onValueChange={(value) => setExportFormat(value as 'csv' | 'pdf')}
            className="grid grid-cols-2 gap-4"
          >
            <div className="flex items-center space-x-2 border rounded-lg p-4 hover:bg-muted/50 cursor-pointer">
              <RadioGroupItem value="csv" id="csv" />
              <div className="flex items-center space-x-3">
                <FileSpreadsheet className="w-5 h-5 text-green-600" />
                <div>
                  <Label htmlFor="csv" className="cursor-pointer font-medium">CSV</Label>
                  <p className="text-xs text-muted-foreground">Spreadsheet format</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 border rounded-lg p-4 hover:bg-muted/50 cursor-pointer">
              <RadioGroupItem value="pdf" id="pdf" />
              <div className="flex items-center space-x-3">
                <FileText className="w-5 h-5 text-red-600" />
                <div>
                  <Label htmlFor="pdf" className="cursor-pointer font-medium">PDF</Label>
                  <p className="text-xs text-muted-foreground">Document format</p>
                </div>
              </div>
            </div>
          </RadioGroup>
        </div>

        {/* Date Range */}
        <div className="space-y-3">
          <Label className="text-base font-medium">Date Range</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal"
              >
                <Calendar className="mr-2 h-4 w-4" />
                {dateRange?.from ? (
                  dateRange.to ? (
                    <>
                      {format(dateRange.from, 'LLL dd, y')} -{' '}
                      {format(dateRange.to, 'LLL dd, y')}
                    </>
                  ) : (
                    format(dateRange.from, 'LLL dd, y')
                  )
                ) : (
                  'Pick a date range'
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <CalendarComponent
                initialFocus
                mode="range"
                defaultMonth={dateRange?.from}
                selected={dateRange}
                onSelect={setDateRange}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
          <p className="text-xs text-muted-foreground">
            Select the date range for transactions to export
          </p>
        </div>

        {/* Include Current Filters */}
        {getActiveFiltersCount() > 0 && (
          <div className="space-y-3">
            <Label className="text-base font-medium">Current Filters</Label>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="includeFilters"
                checked={includeFilters}
                onCheckedChange={(checked) => setIncludeFilters(checked as boolean)}
              />
              <Label htmlFor="includeFilters" className="text-sm">
                Include current filters ({getActiveFiltersCount()} active)
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Apply your current search and filter criteria to the export
            </p>
          </div>
        )}

        {/* Export Preview */}
        <div className="bg-muted/50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">Export Preview</h4>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>• Format: {exportFormat.toUpperCase()}</p>
            <p>• Date range: {dateRange?.from ? format(dateRange.from, 'MMM d') : 'Start'} - {dateRange?.to ? format(dateRange.to, 'MMM d, y') : 'End'}</p>
            {includeFilters && getActiveFiltersCount() > 0 && (
              <p>• Filters: {getActiveFiltersCount()} applied</p>
            )}
            <p>• Estimated size: Based on selected criteria</p>
          </div>
        </div>

        {/* Export Fields (for CSV) */}
        {exportFormat === 'csv' && (
          <div className="space-y-3">
            <Label className="text-base font-medium">Included Fields</Label>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center space-x-2">
                <Checkbox checked disabled />
                <span>Transaction ID</span>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox checked disabled />
                <span>Date & Time</span>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox checked disabled />
                <span>Type</span>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox checked disabled />
                <span>Amount</span>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox checked disabled />
                <span>Currency</span>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox checked disabled />
                <span>Status</span>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox checked disabled />
                <span>Description</span>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox checked disabled />
                <span>Counterparty</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              All transaction fields will be included in the export
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-3 pt-4">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isExporting}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={isExporting || !dateRange?.from || !dateRange?.to}
            className="flex-1"
          >
            {isExporting ? (
              'Exporting...'
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export {exportFormat.toUpperCase()}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TransactionExport; 